/* eslint-disable import/prefer-default-export */
import { createSelector } from 'reselect';
import { buildPrivateImageUrl } from '../../common/util/buildImageUrl';
import defaultAvatar from '../../styles/images/default-avatar.png';

const activeUsers = (state) => state.userManagement.activeUsers;
const deactivatedUsers = (state) => state.userManagement.deactivatedUsers;

export function enrichUserType(userList, entity, sourceAttr, targetAttr) {
  if (entity[sourceAttr]) {
    if (entity[sourceAttr] === 1) {
      entity[targetAttr] = {
        displayName: 'System',
        imageId: null,
        disabled: false,
        disableMessage: null,
      };
    } else {
      const _user = userList.find((user) => entity[sourceAttr] === user.userId);
      entity[targetAttr] = _user
        ? {
            displayName: `${_user.firstName} ${_user.lastName}`,
            imageId: _user.imageId,
            disabled: _user?.disabled,
            disableMessage: _user?.disableMessage,
          }
        : null;
    }
  }
}
export function enrichUserModifierType(
  userList,
  entity,
  sourceAttr,
  targetAttr
) {
  if (entity[sourceAttr]) {
    const _user = userList.find((user) => entity[sourceAttr] === user.userId);
    entity[targetAttr] = _user
      ? {
          displayName: `${_user.firstName} ${_user.lastName}`,
          imageId: _user.imageId,
          disabled: _user?.disabled,
          disableMessage: _user?.disableMessage,
        }
      : null;
  }
}

/**
 * Enriches a list of user ids
 * @param {*} userList
 * @param {*} entity
 * @param {*} sourceAttr
 * @param {*} targetAttr
 */
export function enrichUserTypeList(userList, entity, sourceAttr, targetAttr) {
  if (entity[sourceAttr]) {
    entity[targetAttr] = entity[sourceAttr]
      .map((userId) => {
        const _user = userList.find((user) => userId === user.userId);
        return _user
          ? {
              displayName: `${_user.firstName} ${_user.lastName}`,
              imageId: _user.imageId,
              disabled: _user?.disabled,
              disableMessage: _user?.disableMessage,
            }
          : null;
      })
      .filter((enrichedUser) => !!enrichedUser);
  }
}

export const activeUserSelector = createSelector(activeUsers, (userList) => {
  return userList.filter((user) => user.roleName !== 'Viewer');
});

export const allUserSelector = createSelector(
  activeUsers,
  deactivatedUsers,
  (activeUserList, deactivatedUserList) => {
    return [
      ...activeUserList.map((user) =>
        user.roleName !== 'Viewer'
          ? { ...user, disabled: false }
          : { ...user, disabled: true, disableMessage: 'Viewer' }
      ),
      ...deactivatedUserList.map((user) => {
        return { ...user, disabled: true, disableMessage: 'Deactivated' };
      }),
    ].sort((u1, u2) => {
      if (u1.disabled === u2.disabled) {
        return `${u1.firstName} ${u1.lastName}`
          .trim()
          .localeCompare(`${u2.firstName} ${u2.lastName}`.trim());
      }
      return u1.disabled - u2.disabled;
    });
  }
);

export const allUserSelectorWithViewers = createSelector(
  activeUsers,
  deactivatedUsers,
  (activeUserList, deactivatedUserList) => {
    return [
      ...activeUserList.map((user) =>
        user.roleName !== 'Viewer'
          ? { ...user, disabled: false }
          : { ...user, disabled: false, disableMessage: 'Viewer' }
      ),
      ...deactivatedUserList.map((user) => {
        return { ...user, disabled: true, disableMessage: 'Deactivated' };
      }),
    ].sort((u1, u2) => {
      if (u1.disabled === u2.disabled) {
        return `${u1.firstName} ${u1.lastName}`
          .trim()
          .localeCompare(`${u2.firstName} ${u2.lastName}`.trim());
      }
      return u1.disabled - u2.disabled;
    });
  }
);

export const enrichedAllOwnerSelector = createSelector(
  allUserSelector,
  (users) => {
    return users.map((user) => {
      return { ...user, primary: false };
    });
  }
);

export const enrichedRichTextUsersSelector = createSelector(
  allUserSelector,
  (users) => {
    const filteredUsers = [];
    users.forEach(async (user) => {
      if (user.enabled && !user.disabled) {
        const result = user.imageId
          ? await buildPrivateImageUrl(user.imageId)
          : defaultAvatar;
        filteredUsers.push({ ...user, imageId: result });
      }
    });
    return filteredUsers;
  }
);

export const enrichedRichTextUsersSelectorWithViewers = createSelector(
  allUserSelectorWithViewers,
  (users) => {
    const filteredUsers = [];
    users.forEach(async (user) => {
      if (user.enabled && !user.disabled) {
        const result = user.imageId
          ? await buildPrivateImageUrl(user.imageId)
          : defaultAvatar;
        filteredUsers.push({ ...user, imageId: result });
      }
    });
    return filteredUsers;
  }
);

export const selectLoggedInUser = createSelector(
  (state) => state.currentUser,
  (currentUser) => currentUser.user
);
